import { useMemo } from 'react';
import { FileUpload as Base, type FileUploadProps, type MediaValue } from '@repletoryreach/sdk-media';
import { InputWrapper } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../i18n/I18nProvider';

/**
 * The console's file field: `@repletoryreach/sdk-media`'s uploader with this
 * app's translations and label conventions wired in.
 *
 * The package defaults its strings to English so that no copy needing
 * translation lives outside `src/i18n/catalog.ts`; this wrapper is where the
 * catalogue meets the component, so pages never pass labels themselves.
 *
 * `description` renders under the control, per the house rule for form hints.
 */
export function FileUpload({
  label,
  description,
  error,
  required,
  ...rest
}: FileUploadProps & {
  label?: string;
  description?: string;
  error?: string;
  required?: boolean;
}) {
  const t = useT();

  const labels = useMemo(
    () => ({
      prompt: t('upload.prompt'),
      library: t('upload.library'),
      libraryTitle: t('upload.library.title'),
      librarySearch: t('upload.library.search'),
      libraryEmpty: t('upload.library.empty'),
      libraryLoadMore: t('upload.library.more'),
      uploading: t('upload.uploading'),
      cancel: t('action.cancel'),
      retry: t('action.retry'),
      replace: t('upload.replace'),
      remove: t('action.remove'),
      rename: t('upload.rename'),
      renameTitle: t('upload.rename.title'),
      renamePlaceholder: t('upload.rename.placeholder'),
      save: t('action.save'),
      copy: t('upload.copy'),
      copied: t('upload.copied'),
      open: t('action.open'),
      view: t('upload.view'),
      select: t('upload.select'),
      deleteFile: t('upload.delete'),
      deleteConfirm: t('upload.delete.confirm'),
      filterAll: t('upload.filter.all'),
      filterImage: t('upload.filter.image'),
      filterVideo: t('upload.filter.video'),
      filterDocument: t('upload.filter.document'),
      errorTooLarge: t('upload.error.tooLarge'),
      errorType: t('upload.error.type'),
      errorInfected: t('upload.error.infected'),
      errorScanUnavailable: t('upload.error.scanUnavailable'),
      errorNotConfigured: t('upload.error.notConfigured'),
      errorGeneric: t('upload.error.generic')
    }),
    [t]
  );

  const field = <Base {...rest} labels={labels} invalid={!!error} />;

  if (!label && !description && !error) return field;

  return (
    <InputWrapper label={label} description={description} error={error} required={required}>
      {() => field}
    </InputWrapper>
  );
}

export type { MediaValue };
